import Player from '@vimeo/player'
import createLazyElementPromise from '../lazy-loading/create-lazy-element-promise'
import ObjectFitController from '../layout/object-fit-controller'
import defaults from 'lodash/defaults'

// This class facilitates the following:
// Lazy loading: Optionally waits for the containing element to be visible and near the viewport (lazy option)
// Object fit: Fits the video element inside the container and updates the fit whenever the viewport size changes
// Click to play and pause
// Initiating the vimeo player

export default class VimeoController {
  constructor (containerElement,
    options) {
    this._containerElement = containerElement
    this._settings = options

    const defaultOptions = {
      lazy: true,
      objectFit: 'responsive', // must be  'cover' / 'contain' / 'responsive' / null
      clickToPlayPause: false,
      playerOptions: {} // pass Vimeo player options. See: https://developer.vimeo.com/player/sdk/embed
    }
    this._settings = defaults(options, defaultOptions)

    // the _allowLoad promise resolves once the player is allowed to be loaded.
    const allowLoadPromise = this._waitUntilLoadAllowed()

    // The player property (Promise<Player>). Can be publicly accessed to control the player.
    this.player = allowLoadPromise.then(() => {
      return this._createVimeoPlayer()
    })

    if (this._settings.objectFit != null) {
      this._initiateFitting()
    }

    if (this._settings.clickToPlayPause) {
      this._inititiateClickToPlayPause()
    }

    this.player.then(() => {
      this._containerElement.classList.add('vimeo-ready')
    })
  }

  _waitUntilLoadAllowed () {
    return new Promise((resolve, reject) => {
      if (!this._settings.lazy) {
        // Not lazy: resolve immediately
        resolve()
      } else {
        // Lazy: create a promise that will be resolved when the container element is visible and near the viewport
        resolve(createLazyElementPromise(this._containerElement))
      }
    })
  }

  _getIframe () {
    return this.player.then(() => {
      const iframe = this._containerElement.querySelector('iframe')
      return iframe
    })
  }

  _initiateFitting () {
    // This method will fit the iframe to the container by one of the supported methods:
    // * Cover: the iframe will cover the container, edges will be cropped. Positioning / centering should be handled by the CSS.
    // * Contain: the iframe will fit inside the container, leftover space might exist. Positioning / centering should be handled by the CSS.
    // * Responsive: a responsive aspect ratio box will be added to the container. The box has the same aspect ratio as the original iframe. The iframe is stretched over the container.
    // Fit will be maintained whenever the window resizes
    const objectFit = this._settings.objectFit
    if (objectFit === null) return

    return this._getIframe().then((iframe) => {
      if (objectFit === 'cover' || objectFit === 'contain') {
        // eslint-disable-next-line no-new
        new ObjectFitController(
          this._containerElement,
          iframe,
          {
            fitting: objectFit,
            aspectRatio: 'attributes',
            result: 'attributes'
          })
      } else if (objectFit === 'responsive') {
        // Add aspect ratio element
        const aspectRatio = parseInt(iframe.getAttribute('width')) / parseInt(iframe.getAttribute('height'))
        const aspectElement = document.createElement('div')
        aspectElement.classList.add('vimeo-aspect-box')
        aspectElement.style.paddingTop = (100 / aspectRatio) + '%'
        Object.assign(iframe.style, {
          position: 'absolute',
          left: '0',
          top: '0',
          width: '100%',
          height: '100%'
        })
        this._containerElement.appendChild(aspectElement)
      }
    })
  }

  _inititiateClickToPlayPause () {
    const clickCoverElement = document.createElement('div')
    Object.assign(clickCoverElement.style, {
      position: 'absolute',
      left: '0',
      top: '0',
      width: '100%',
      height: '100%'
    })

    this.player.then((player) => {
      this._containerElement.appendChild(clickCoverElement)
      clickCoverElement.addEventListener('click', () => {
        player.getPaused().then(function (paused) {
          if (paused) {
            player.play()
          } else {
            player.pause()
          }
        })
      })
    })
  }

  _createVimeoPlayer () {
    return new Promise((resolve, reject) => {
      this._containerElement.classList.add('vimeo-loading')
      const player = new Player(this._containerElement, this._settings.playerOptions)
      player.on('loaded', () => {
        resolve(player)
      })
      player.on('play', () => {
        this._containerElement.classList.remove('vimeo-loading')
      })
      setTimeout(() => {
        this._containerElement.classList.remove('vimeo-loading')
      }, 4000)
    })
  }
}
